import { useState } from 'react';
import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import { supabase } from '@/lib/supabase';
import { getStatusMeta, submissionStatusMap } from '@/lib/statusMap';
import { openSubmissionFile, downloadSubmissionFile, formatFileSize } from '@/lib/fileDownload';
import type { Assignment } from '@/hooks/useDashboardData';

export interface Submission {
  id: string;
  assignment_id: string;
  student_id: string;
  status: string;
  note: string | null;
  submitted_at: string;
  file_url: string | null;
  file_path: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  link: string | null;
}

export interface StudentRecord {
  id: string;
  group_id: string | null;
  parent_name: string | null;
}

interface SubmissionsModalProps {
  open: boolean;
  onClose: () => void;
  assignment: Assignment | null;
  submissions: Submission[];
  students: StudentRecord[];
  studentNames: Record<string, string>;
  groupName: string;
  onChanged: () => void;
}

const REVIEW_STATUSES = [
  { value: 'teslim_edildi', label: 'Teslim Edildi (Bekliyor)' },
  { value: 'kontrol_edildi', label: 'Kontrol Edildi (Onay)' },
  { value: 'eksik', label: 'Eksik' },
  { value: 'yeniden', label: 'Yeniden Yapılmalı' },
];

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

function SubmissionRow({
  submission,
  studentName,
  onChanged,
}: {
  submission: Submission;
  studentName: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(submission.status);
  const [note, setNote] = useState(submission.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fileErr, setFileErr] = useState<string | null>(null);

  async function handleOpenFile() {
    if (!submission.file_path) return;
    setBusy(true);
    setFileErr(null);
    try {
      await openSubmissionFile(submission.file_path);
    } catch (err) {
      setFileErr((err as Error).message || 'Dosya açılamadı.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDownloadFile() {
    if (!submission.file_path) return;
    setBusy(true);
    setFileErr(null);
    try {
      await downloadSubmissionFile(submission.file_path, submission.file_name ?? undefined);
    } catch (err) {
      setFileErr((err as Error).message || 'Dosya indirilemedi.');
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('zehrahoca_assignment_submissions')
        .update({ status, note: note.trim() || null })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      setEditing(false);
      onChanged();
    } catch (err) {
      setError((err as Error).message || 'Güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-background-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
            {studentName.charAt(0)}
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-900">{studentName}</p>
            <p className="mt-0.5 text-xs text-foreground-500">{formatDateTime(submission.submitted_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge meta={getStatusMeta(submission.status, submissionStatusMap)} />
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-check-double-line" />
              Kontrol Et
            </button>
          )}
        </div>
      </div>

      {!editing && submission.note && (
        <p className="mt-3 rounded-lg bg-background-100 px-3 py-2 text-sm text-foreground-700">
          <span className="font-semibold">Not: </span>
          {submission.note}
        </p>
      )}

      {!editing && submission.file_name && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-1.5 text-sm text-foreground-800">
            <i className="ri-file-line shrink-0 text-foreground-500" />
            <span className="truncate">{submission.file_name}</span>
            {submission.file_size ? (
              <span className="shrink-0 text-xs text-foreground-400">({formatFileSize(submission.file_size)})</span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleOpenFile}
              disabled={busy}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition hover:bg-primary-100 disabled:opacity-70 cursor-pointer"
            >
              {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-eye-line" />}
              Dosyayı Aç
            </button>
            <button
              type="button"
              onClick={handleDownloadFile}
              disabled={busy}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-background-300 bg-white px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 disabled:opacity-70 cursor-pointer"
            >
              <i className="ri-download-2-line" />
              İndir
            </button>
          </div>
        </div>
      )}

      {!editing && submission.link && (
        <a
          href={submission.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 transition hover:text-primary-700"
        >
          <i className="ri-link" /> Ek Kaynak Linki
        </a>
      )}

      {!editing && fileErr && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600">
          <i className="ri-error-warning-line mt-0.5" />
          {fileErr}
        </p>
      )}

      {editing && (
        <div className="mt-3 space-y-3 border-t border-background-200 pt-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground-600">Teslim Durumu</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              {REVIEW_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground-600">Öğrenciye Not (opsiyonel)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Öğrenciye görünecek geri bildirim..."
              className="w-full resize-none rounded-lg border border-background-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          {error && (
            <p className="flex items-start gap-1.5 text-xs text-red-600">
              <i className="ri-error-warning-line mt-0.5" />
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setStatus(submission.status);
                setNote(submission.note ?? '');
              }}
              className="whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold text-foreground-600 transition hover:bg-background-100 cursor-pointer"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SubmissionsModal({
  open,
  onClose,
  assignment,
  submissions,
  students,
  studentNames,
  groupName,
  onChanged,
}: SubmissionsModalProps) {
  if (!assignment) return null;

  const groupStudents = students.filter((s) => s.group_id === assignment.group_id);
  const submittedIds = new Set(submissions.map((s) => s.student_id));
  const pendingStudents = groupStudents.filter((s) => !submittedIds.has(s.id));
  const total = groupStudents.length;
  const submittedCount = submissions.length;
  const percent = total > 0 ? Math.round((submittedCount / total) * 100) : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={assignment.title}
      subtitle={`${groupName} · Son teslim: ${formatDate(assignment.deadline)}`}
      maxWidth="max-w-3xl"
    >
      <div className="mb-6 flex items-center gap-4 rounded-xl bg-background-100 px-4 py-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground-900">Teslim Durumu</p>
          <p className="mt-0.5 text-xs text-foreground-500">
            {submittedCount} / {total} öğrenci teslim etti
          </p>
        </div>
        <div className="hidden w-40 sm:block">
          <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
            <div className="h-2 rounded-full bg-primary-500 transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>
        <span className="text-sm font-bold text-foreground-900">{percent}%</span>
      </div>

      <h4 className="mb-3 text-sm font-bold text-foreground-800">
        Teslimler <span className="font-normal text-foreground-500">({submittedCount})</span>
      </h4>

      {submissions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-background-300 bg-background-50 p-8 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-400" />
          <p className="mt-2 text-sm text-foreground-500">Henüz teslim eden öğrenci yok.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <SubmissionRow
              key={s.id}
              submission={s}
              studentName={studentNames[s.student_id] ?? 'Öğrenci'}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}

      {pendingStudents.length > 0 && (
        <>
          <h4 className="mb-3 mt-6 text-sm font-bold text-foreground-800">
            Teslim Etmeyenler <span className="font-normal text-foreground-500">({pendingStudents.length})</span>
          </h4>
          <div className="flex flex-wrap gap-2">
            {pendingStudents.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-700"
              >
                <i className="ri-user-line" />
                {studentNames[s.id] ?? 'Öğrenci'}
              </span>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}